import {
  Box,
  Flex,
  Heading,
  HelpLinkIFrame,
  LinkAnchor,
  mdBumps,
  PrimaryButton,
  smBumps,
  Status,
  Typo,
  useModal,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'

import {
  AccrualType,
  getPremiumByAccrualType,
  PriceOption,
  Quote,
} from '../../../../domain'
import {
  tCovers,
  TKey,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import {
  getOptionalCoverLink,
  getOptionalCoversContentBaseUrl,
  useConfig,
} from '../../../../state/configuration'
import { useProduct } from '../../../product/productContext'

export interface AddOnCardProps {
  cover: PriceOption
  loading?: boolean
  quote: Quote
  onAdd: () => void
}

const tKey = TKey(`components.add-on-card`)

export const AddOnCard: FunctionComponent<AddOnCardProps> = ({
  cover,
  loading,
  quote,
  onAdd,
}) => {
  const { t } = useWriskTranslation()
  const modal = useModal()

  const { scheme } = useProduct()
  const contentBaseUrl = useConfig(getOptionalCoversContentBaseUrl(scheme.schemeCode))
  const link = getOptionalCoverLink(contentBaseUrl, quote.wordingVersion, cover)
  const amount = getPremiumByAccrualType([cover], AccrualType.MONTHLY)

  const onLinkMoreClick = useCallback(
    () =>
      modal.show({
        content: <HelpLinkIFrame src={link} />,
        variant: 'iframe',
      }),
    [modal, link],
  )

  return (
    <Flex
      alignItems='flex-start'
      flexDirection='column'
      mb={smBumps}
      p={mdBumps}
      variant='raised'
      width={1}
    >
      <Status mb={smBumps} variant='info' typoSize='xs'>
        {t(tKey('status'))}
      </Status>
      <Heading mb={smBumps} typoSize='md' variant='h3'>
        {t(tCovers(cover.coverCode, 'name'))}
      </Heading>
      <Box mb={mdBumps} width={1}>
        <Typo mb={smBumps}>{t(tCovers(cover.coverCode, 'description'))}</Typo>
        <LinkAnchor variant='standalone' typoSize='sm' onClick={onLinkMoreClick}>
          {t(tKey('actions.learn-more'))}
        </LinkAnchor>
      </Box>

      <PrimaryButton data-testid='add-on-button' loading={loading} onClick={onAdd}>
        {t(tKey('actions.add'), { amount })}
      </PrimaryButton>
    </Flex>
  )
}
